
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import PageLayout from '@/components/layout/PageLayout';
import MealCompositionCard from '@/components/meal-composition/MealCompositionCard';
import MealCompositionForm from '@/components/meal-composition/MealCompositionForm';
import DeleteCompositionDialog from '@/components/meal-composition/DeleteCompositionDialog';
import { AdaptiveModal } from '@/components/ui/AdaptiveModal';
import { mealCompositionService } from '@/services/mealComposition';
import { recipeService } from '@/services/recipe';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { MEAL_COMPOSITIONS, RECIPES } from '@/constants/queryKeys';

const Compositions = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingComposition, setEditingComposition] = useState<MealCompositionWithRecipes | null>(null);
  const [deletingComposition, setDeletingComposition] = useState<MealCompositionWithRecipes | null>(null);
  const queryClient = useQueryClient();

  const { data: compositions = [], isLoading } = useQuery({
    queryKey: [MEAL_COMPOSITIONS],
    queryFn: mealCompositionService.getMealCompositions,
  });

  const { data: recipes = [] } = useQuery({
    queryKey: [RECIPES],
    queryFn: recipeService.getRecipes,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      mealCompositionService.updateMealComposition(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEAL_COMPOSITIONS] });
      toast.success('Composition updated successfully');
      setEditingComposition(null);
    },
    onError: () => {
      toast.error('Failed to update composition');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => mealCompositionService.deleteMealComposition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEAL_COMPOSITIONS] });
      toast.success('Composition deleted successfully');
      setDeletingComposition(null);
    },
    onError: () => {
      toast.error('Failed to delete composition');
    },
  });

  const filteredCompositions = compositions.filter(composition =>
    composition.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    composition.recipes.some(recipe => 
      recipe.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleEdit = async (data: any) => {
    if (!editingComposition) return;
    await updateMutation.mutateAsync({
      id: editingComposition.id,
      updates: data
    });
  };

  // Check if we should show simplified view (no compositions and no search query)
  const showSimplifiedView = compositions.length === 0 && !searchQuery;

  return (
    <PageLayout
      title="Meal Compositions"
      description="Manage your meal compositions - groups of recipes that work well together"
    >
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Actions - Conditional based on whether we have compositions */}
        {!showSimplifiedView && (
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search compositions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button asChild>
              <Link to="/create-composition">
                <Plus className="h-4 w-4 mr-2" />
                Create Composition
              </Link>
            </Button>
          </div>
        )}

        {/* Compositions Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : filteredCompositions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              {searchQuery ? 'No compositions found matching your search.' : 'No meal compositions yet.'}
            </p>
            {showSimplifiedView ? (
              <Button asChild size="lg">
                <Link to="/create-composition">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Composition
                </Link>
              </Button>
            ) : !searchQuery ? (
              <Button asChild>
                <Link to="/create-composition">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Composition
                </Link>
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompositions.map((composition) => (
              <MealCompositionCard
                key={composition.id}
                composition={composition}
                onEdit={setEditingComposition}
                onDelete={setDeletingComposition}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AdaptiveModal
        isOpen={!!editingComposition}
        onClose={() => setEditingComposition(null)}
        title="Edit Composition"
        size="lg"
        scrollable={true}
      >
        {editingComposition && (
          <MealCompositionForm
            composition={editingComposition}
            recipes={recipes}
            onSubmit={handleEdit}
            onCancel={() => setEditingComposition(null)}
            isLoading={updateMutation.isPending}
          />
        )}
      </AdaptiveModal>

      {/* Delete Dialog */}
      <DeleteCompositionDialog
        composition={deletingComposition}
        isOpen={!!deletingComposition}
        onClose={() => setDeletingComposition(null)}
        onConfirm={(comp) => deleteMutation.mutate(comp.id)}
        isLoading={deleteMutation.isPending}
      />
    </PageLayout>
  );
};

export default Compositions;
